import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Loader2, Plus, Save } from 'lucide-react';

const slugify = (value) =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export default function AdminProductCategoriesPanel() {
  const { api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    id: '',
    url_slug: '',
    description: '',
    idTouched: false,
    slugTouched: false,
  });
  const [editDrafts, setEditDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/product-categories');
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/product-categories', {
        id: draft.id.trim(),
        url_slug: draft.url_slug.trim(),
        name: draft.name.trim(),
        description: draft.description.trim(),
        is_active: true,
      });
      toast.success('Category created');
      setShowCreate(false);
      setDraft({ name: '', id: '', url_slug: '', description: '', idTouched: false, slugTouched: false });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  const saveCategory = async (category) => {
    const edits = editDrafts[category.id];
    if (!edits) return;

    const nextSlug = edits.url_slug ?? category.url_slug;
    if (
      !category.is_system
      && edits.url_slug
      && nextSlug !== category.url_slug
    ) {
      const confirmed = window.confirm(
        `Change the URL slug from "${category.url_slug}" to "${nextSlug}"?\n\n`
        + `Existing links to /templates/${category.url_slug} will stop working.`,
      );
      if (!confirmed) return;
    }

    setSavingId(category.id);
    try {
      await api.patch(`/admin/product-categories/${category.id}`, edits);
      toast.success('Category updated');
      setEditDrafts((prev) => {
        const next = { ...prev };
        delete next[category.id];
        return next;
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update category');
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (category, checked) => {
    setSavingId(category.id);
    try {
      await api.patch(`/admin/product-categories/${category.id}`, { is_active: checked });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update category');
    } finally {
      setSavingId(null);
    }
  };

  const moveCategory = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= categories.length) return;
    const ordered = [...categories];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    setSavingId('reorder');
    try {
      await api.patch('/admin/product-categories/reorder', {
        ordered_ids: ordered.map((c) => c.id),
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reorder categories');
    } finally {
      setSavingId(null);
    }
  };

  const deleteCategory = async (category) => {
    if (category.is_system) return;
    if (!window.confirm(`Delete category "${category.name}"? This cannot be undone.`)) return;
    setSavingId(category.id);
    try {
      await api.delete(`/admin/product-categories/${category.id}`);
      toast.success('Category deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot delete category');
    } finally {
      setSavingId(null);
    }
  };

  const setEditField = (id, field, value) => {
    setEditDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Store Categories</h2>
          <p className="text-sm text-muted-foreground">
            Sidebar labels and URLs for the Templates &amp; Toolkits store. &quot;All&quot; is built into the storefront.
          </p>
        </div>
        <Button onClick={() => setShowCreate((v) => !v)} data-testid="product-categories-new-btn">
          <Plus className="mr-2 h-4 w-4" />
          New Category
        </Button>
      </div>

      {showCreate && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>New category</CardTitle>
            <CardDescription>URL slug must not match an existing product slug.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="cat-name">Display name</Label>
                <Input
                  id="cat-name"
                  value={draft.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setDraft((prev) => ({
                      ...prev,
                      name,
                      id: prev.idTouched ? prev.id : slugify(name),
                      url_slug: prev.slugTouched ? prev.url_slug : slugify(name),
                    }));
                  }}
                  required
                />
              </div>
              <div>
                <Label htmlFor="cat-id">Internal id</Label>
                <Input
                  id="cat-id"
                  value={draft.id}
                  onChange={(e) => setDraft((prev) => ({ ...prev, id: slugify(e.target.value), idTouched: true }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="cat-slug">URL slug</Label>
                <Input
                  id="cat-slug"
                  value={draft.url_slug}
                  onChange={(e) => setDraft((prev) => ({ ...prev, url_slug: slugify(e.target.value), slugTouched: true }))}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cat-desc">Description (optional)</Label>
                <Input
                  id="cat-desc"
                  value={draft.description}
                  onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2 flex gap-2">
                <Button type="submit" disabled={creating} data-testid="product-categories-create-submit">
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create category
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="card-elevated">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Internal id</TableHead>
                <TableHead>URL slug</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Visible</TableHead>
                <TableHead>Order</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category, index) => {
                const edits = editDrafts[category.id] || {};
                const nameValue = edits.name ?? category.name;
                const slugValue = edits.url_slug ?? category.url_slug;
                const hasEdits = Boolean(editDrafts[category.id]);
                return (
                  <TableRow key={category.id} data-testid={`product-category-row-${category.id}`}>
                    <TableCell>
                      <Input
                        value={nameValue}
                        onChange={(e) => setEditField(category.id, 'name', e.target.value)}
                        className="min-w-[160px]"
                      />
                      {category.is_system && (
                        <Badge variant="secondary" className="mt-1 text-[10px]">System</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{category.id}</code>
                      <p className="text-[10px] text-muted-foreground mt-1">Locked after creation</p>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={slugValue}
                        disabled={category.is_system}
                        onChange={(e) => setEditField(category.id, 'url_slug', slugify(e.target.value))}
                        className="min-w-[140px]"
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">/templates/{slugValue}</p>
                    </TableCell>
                    <TableCell>{category.product_count ?? 0}</TableCell>
                    <TableCell>
                      <Switch
                        checked={category.is_active !== false}
                        disabled={savingId === category.id}
                        onCheckedChange={(checked) => toggleActive(category, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={index === 0 || savingId === 'reorder'} onClick={() => moveCategory(index, -1)}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={index >= categories.length - 1 || savingId === 'reorder'} onClick={() => moveCategory(index, 1)}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {hasEdits && (
                        <Button type="button" size="sm" disabled={savingId === category.id} onClick={() => saveCategory(category)}>
                          <Save className="mr-1 h-3 w-3" /> Save
                        </Button>
                      )}
                      {!category.is_system && (
                        <Button type="button" variant="ghost" size="sm" disabled={savingId === category.id} onClick={() => deleteCategory(category)}>
                          Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
